import React, { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { 
  FileText, RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle,
  Printer, Filter, RotateCcw, Eye, ChevronDown, Copy
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { getBranchAuthHeaders } from '@/utils/branchApi';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_CONFIG = {
  QUEUED: { color: 'bg-blue-500', icon: Clock, label: 'Queued' },
  SENT: { color: 'bg-yellow-500', icon: RefreshCw, label: 'Sent' },
  PRINTED: { color: 'bg-green-500', icon: CheckCircle, label: 'Printed' },
  FAILED: { color: 'bg-red-500', icon: XCircle, label: 'Failed' }
};

export default function PrintJobsPage() {
  const [jobs, setJobs] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [printerFilter, setPrinterFilter] = useState('');
  const [includeDeadLetter, setIncludeDeadLetter] = useState(true);

  const fetchJobs = useLatestCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/kitchen/print-jobs?limit=100`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (printerFilter) url += `&printer_id=${printerFilter}`;
      url += `&include_dead_letter=${includeDeadLetter}`;
      
      const res = await fetch(url, {
        headers: getBranchAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setJobs(data);
      }
    } catch (err) {
      toast.error('Failed to load print jobs');
    } finally {
      setLoading(false);
    }
  });

  const fetchPrinters = async () => {
    try {
      const res = await fetch(`${API_URL}/api/kitchen/printers`, {
        headers: getBranchAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setPrinters(data);
      }
    } catch (err) {
      console.error('Failed to load printers');
    }
  };

  useEffect(() => {
    fetchPrinters();
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    fetchJobs();
  }, [statusFilter, printerFilter, includeDeadLetter, fetchJobs]);

  const retryJob = async (jobId) => {
    try {
      const res = await fetch(`${API_URL}/api/kitchen/print-jobs/${jobId}/retry`, {
        method: 'POST',
        headers: getBranchAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Job queued for retry');
        fetchJobs();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to retry job');
      }
    } catch (err) {
      toast.error('Error retrying job');
    }
  };

  // P3: Reprint job with reason
  const reprintJob = async (jobId, reason = '') => {
    try {
      const res = await fetch(`${API_URL}/api/kitchen/print-jobs/${jobId}/reprint`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...getBranchAuthHeaders() 
        },
        body: JSON.stringify({ reason })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Reprint job created: ${data.new_job_id.slice(0, 8)}...`);
        fetchJobs();
        setDetailOpen(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to reprint job');
      }
    } catch (err) {
      toast.error('Error creating reprint');
    }
  };

  const openJobDetail = async (job) => {
    try {
      const res = await fetch(`${API_URL}/api/kitchen/print-jobs/${job.id}`, {
        headers: getBranchAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setSelectedJob(data);
        setDetailOpen(true);
      }
    } catch (err) {
      toast.error('Failed to load job details');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  // Stats
  const stats = {
    queued: jobs.filter(j => j.status === 'QUEUED').length,
    sent: jobs.filter(j => j.status === 'SENT').length,
    printed: jobs.filter(j => j.status === 'PRINTED').length,
    failed: jobs.filter(j => j.status === 'FAILED').length,
    deadLetter: jobs.filter(j => j.dead_letter).length
  };

  return (
    <div data-testid="print-jobs-page" className="p-6 dark:bg-[#011627]">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 dark:text-[#d6deeb]">
            <FileText className="w-6 h-6" /> Print Jobs
          </h1>
          <p className="text-muted-foreground dark:text-[#7fdbca]">Monitor and manage print job queue</p>
        </div>
        <Button onClick={fetchJobs} variant="outline">
          <RefreshCw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Queued</p>
                <p className="text-2xl font-bold">{stats.queued}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Sent</p>
                <p className="text-2xl font-bold">{stats.sent}</p>
              </div>
              <RefreshCw className="w-8 h-8 text-yellow-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Printed</p>
                <p className="text-2xl font-bold">{stats.printed}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Failed</p>
                <p className="text-2xl font-bold">{stats.failed}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className={stats.deadLetter > 0 ? 'border-red-500' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Dead Letter</p>
                <p className="text-2xl font-bold">{stats.deadLetter}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Filter className="w-5 h-5 text-muted-foreground" />
            
            <div className="w-40">
              <Select value={statusFilter || 'ALL'} onValueChange={(val) => setStatusFilter(val === 'ALL' ? '' : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Statuses</SelectItem>
                  <SelectItem value="QUEUED">Queued</SelectItem>
                  <SelectItem value="SENT">Sent</SelectItem>
                  <SelectItem value="PRINTED">Printed</SelectItem>
                  <SelectItem value="FAILED">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-48">
              <Select value={printerFilter || 'ALL'} onValueChange={(val) => setPrinterFilter(val === 'ALL' ? '' : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Printers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Printers</SelectItem>
                  {printers.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="deadLetter"
                checked={includeDeadLetter}
                onChange={(e) => setIncludeDeadLetter(e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="deadLetter">Include Dead Letter</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Jobs List */}
      {loading ? (
        <div className="text-center py-10">
          <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
        </div>
      ) : jobs.length === 0 ? (
        <Card>
          <CardContent className="text-center py-10">
            <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-medium">No print jobs found</h3>
            <p className="text-muted-foreground">Jobs will appear here when created</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {jobs.map(job => {
            const StatusIcon = STATUS_CONFIG[job.status]?.icon || Clock;
            const statusColor = STATUS_CONFIG[job.status]?.color || 'bg-gray-500';
            
            return (
              <Card 
                key={job.id} 
                className={cn(
                  'transition-colors hover:bg-muted/50',
                  job.dead_letter && 'border-red-500 bg-red-50/50 dark:bg-red-950/10'
                )}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Status Icon */}
                      <div className={cn('p-2 rounded-full', statusColor)}>
                        <StatusIcon className="w-4 h-4 text-white" />
                      </div>
                      
                      {/* Job Info */}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{job.job_type}</span>
                          {job.ticket_no && (
                            <Badge variant="outline">Ticket #{job.ticket_no}</Badge>
                          )}
                          {job.dead_letter && (
                            <Badge variant="destructive">Dead Letter</Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground flex items-center gap-2">
                          <Printer className="w-3 h-3" />
                          {job.printer_name || 'Unknown Printer'}
                          <span>•</span>
                          <Clock className="w-3 h-3" />
                          {formatDate(job.created_at)}
                        </div>
                      </div>
                    </div>
                    
                    {/* Right Side */}
                    <div className="flex items-center gap-2">
                      {/* P3: Reprint badge */}
                      {job.is_reprint && (
                        <Badge variant="outline" className="dark:border-[#7fdbca] dark:text-[#7fdbca]">
                          Reprint
                        </Badge>
                      )}
                      
                      {/* Attempts */}
                      <Badge variant="secondary" className="dark:bg-[#1d3b53] dark:text-[#d6deeb]">
                        {job.attempts}/{job.max_attempts} attempts
                      </Badge>
                      
                      {/* Status Badge */}
                      <Badge className={cn(statusColor, 'text-white')}>
                        {STATUS_CONFIG[job.status]?.label || job.status}
                      </Badge>
                      
                      {/* Actions */}
                      {(job.status === 'FAILED' || job.dead_letter) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => retryJob(job.id)}
                          className="dark:border-[#1d3b53] dark:text-[#d6deeb] dark:hover:bg-[#1d3b53]"
                        >
                          <RotateCcw className="w-4 h-4 mr-1" /> Retry
                        </Button>
                      )}
                      
                      {/* P3: Reprint button for completed jobs */}
                      {job.status === 'PRINTED' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => reprintJob(job.id)}
                          className="dark:border-[#1d3b53] dark:text-[#d6deeb] dark:hover:bg-[#1d3b53]"
                          title="Reprint this job"
                        >
                          <Copy className="w-4 h-4 mr-1" /> Reprint
                        </Button>
                      )}
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openJobDetail(job)}
                        className="dark:text-[#d6deeb] dark:hover:bg-[#1d3b53]"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Error Message */}
                  {job.last_error && (
                    <div className="mt-2 p-2 bg-red-50 dark:bg-red-950/20 rounded text-sm text-red-600 dark:text-red-400">
                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                      {job.last_error}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Job Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Print Job Details</DialogTitle>
          </DialogHeader>
          
          {selectedJob && (
            <ScrollArea className="flex-1">
              <div className="space-y-4 p-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Job ID</Label>
                    <p className="font-mono text-sm">{selectedJob.id}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Job Type</Label>
                    <p>{selectedJob.job_type}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Printer</Label>
                    <p>{selectedJob.printer_name}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Ticket</Label>
                    <p>{selectedJob.ticket_no ? `#${selectedJob.ticket_no}` : '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Status</Label>
                    <Badge className={cn(STATUS_CONFIG[selectedJob.status]?.color, 'text-white')}>
                      {selectedJob.status}
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Attempts</Label>
                    <p>{selectedJob.attempts} / {selectedJob.max_attempts}</p>
                  </div>
                </div>
                
                {/* Timestamps */}
                <Collapsible defaultOpen>
                  <CollapsibleTrigger className="flex items-center gap-2 font-medium">
                    <ChevronDown className="w-4 h-4" />
                    Timestamps
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2 grid grid-cols-2 gap-2 text-sm">
                    <div>Created: {formatDate(selectedJob.created_at)}</div>
                    <div>Sent: {formatDate(selectedJob.sent_at)}</div>
                    <div>Printed: {formatDate(selectedJob.printed_at)}</div>
                    <div>Failed: {formatDate(selectedJob.failed_at)}</div>
                  </CollapsibleContent>
                </Collapsible>
                
                {/* Error Info */}
                {selectedJob.last_error && (
                  <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded">
                    <Label className="text-red-600">Last Error</Label>
                    <p className="text-sm text-red-600">{selectedJob.last_error}</p>
                  </div>
                )}
                
                {/* Dead Letter Info */}
                {selectedJob.dead_letter && (
                  <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded border border-red-500">
                    <Label className="text-red-600">Dead Letter Reason</Label>
                    <p className="text-sm text-red-600">{selectedJob.dlq_reason}</p>
                  </div>
                )}
                
                {/* Payload Preview */}
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center gap-2 font-medium">
                    <ChevronDown className="w-4 h-4" />
                    Print Payload
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2">
                    <div className="bg-white dark:bg-gray-900 border rounded p-3 font-mono text-xs whitespace-pre-wrap max-h-64 overflow-y-auto">
                      {selectedJob.payload_preview}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
                
                {/* Actions */}
                {(selectedJob.status === 'FAILED' || selectedJob.dead_letter) && (
                  <div className="pt-4">
                    <Button onClick={() => { retryJob(selectedJob.id); setDetailOpen(false); }}>
                      <RotateCcw className="w-4 h-4 mr-2" /> Retry This Job
                    </Button>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
